import {
  DeliveryInfoInput,
  DiningOptionBehavior,
  DiningOptionFragmentFragment,
  FulfillmentType
} from 'src/apollo/onlineOrdering'

import { OORestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { Cart } from 'public/components/online_ordering/types'
import { CartDeliveryInfo } from './FulfillmentContext'

export const getPersistedDeliveryInfo = (
  storageKey: string
): DeliveryInfoInput | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return undefined
    return JSON.parse(raw) as DeliveryInfoInput
  } catch {
    return undefined
  }
}

/**
 * Cleans delivery info by extracting only the fields that DeliveryInfoInput expects.
 * This is extra-declarative to remove all extra properties that might cause schema validation issues.
 */
export const cleanDeliveryInfo = (
  deliveryInfo: CartDeliveryInfo | null | undefined
): DeliveryInfoInput | null => {
  if (!deliveryInfo) {
    return null
  }

  const {
    address1,
    address2,
    administrativeArea,
    city,
    country,
    latitude,
    longitude,
    notes,
    state,
    zipCode
  } = deliveryInfo

  return {
    address1,
    address2,
    administrativeArea,
    city,
    country,
    latitude,
    longitude,
    notes,
    state,
    zipCode
  }
}

export const getQuoteTime = (
  diningOptionBehavior: DiningOptionBehavior,
  restaurant: OORestaurant,
  cart?: Cart | null
) =>
  diningOptionBehavior === DiningOptionBehavior.Delivery
    ? cart?.deliveryQuoteTime || restaurant.minimumDeliveryTime
    : cart?.takeoutQuoteTime || restaurant.minimumTakeoutTime

export const getInitialFulfillmentForDiningOption = (
  diningOption: DiningOptionFragmentFragment | undefined | null,
  validDeliveryAddress?: DeliveryInfoInput | null
) => {
  if (!diningOption) {
    return null
  }
  const fulfillmentType = diningOption.asapSchedule?.availableNow
    ? FulfillmentType.Asap
    : FulfillmentType.Future
  const fulfillmentDateTime =
    fulfillmentType === FulfillmentType.Future
      ? diningOption?.futureSchedule?.dates?.find((date) => date?.times?.length)
          ?.times?.[0]?.time
      : undefined
  if (fulfillmentType === FulfillmentType.Future && !fulfillmentDateTime) {
    return null
  }
  return {
    cartFulfillmentData: {
      fulfillmentType,
      diningOptionBehavior: diningOption.behavior,
      deliveryInfo:
        diningOption.behavior === DiningOptionBehavior.Delivery
          ? validDeliveryAddress
          : undefined,
      fulfillmentDateTime
    },
    scheduleData: {
      asapAvailableNow: diningOption.asapSchedule?.availableNow || false,
      futureScheduleDates: diningOption.futureSchedule?.dates || []
    }
  }
}

/**
 * Get the default fulfillment info as whatever is available first:
 *  1. takeout ASAP
 *  2. delivery ASAP
 *  3. takeout scheduled
 *  4. delivery scheduled
 */
export const getDefaultFulfillmentInfo = (
  rxDefaultDiningBehavior: DiningOptionFragmentFragment | undefined | null,
  diningOptionsByBehavior: {
    takeout: DiningOptionFragmentFragment | undefined
    delivery: DiningOptionFragmentFragment | undefined
  },
  validDeliveryAddress?: DeliveryInfoInput | null
) => {
  const defaultFulfillment = getInitialFulfillmentForDiningOption(
    rxDefaultDiningBehavior,
    validDeliveryAddress
  )
  if (defaultFulfillment) {
    return defaultFulfillment
  }
  const orderOfPreference = ['takeout', 'delivery']

  for (const behavior of orderOfPreference) {
    const diningOption =
      diningOptionsByBehavior[behavior as keyof typeof diningOptionsByBehavior]
    if (diningOption?.asapSchedule?.availableNow) {
      return getInitialFulfillmentForDiningOption(
        diningOption,
        validDeliveryAddress
      )
    }
  }

  for (const behavior of orderOfPreference) {
    const diningOption =
      diningOptionsByBehavior[behavior as keyof typeof diningOptionsByBehavior]
    if (diningOption?.futureSchedule?.dates?.length) {
      return getInitialFulfillmentForDiningOption(
        diningOption,
        validDeliveryAddress
      )
    }
  }

  return null
}
